import React from "react";
import { Progress, Tooltip } from "antd";
import { SB_COLORS } from "theme/SbThemeV2";
import { CHART_ACCENT_COLOR, CHART_GREY_START, CHART_GREY_END, interpolateColor, byValueDesc } from "../dashboardData";
import {
  RowsWrap,
  LabelColumn,
  RowLabelText,
  ChartArea,
  GridLinesRow,
  GridLine,
  BarsColumn,
  BarRow,
  BarTrack,
  BarValueText,
  AxisRow,
  AxisTickText,
} from "../Dashboard.styles";

// Manually detects overflow (scrollWidth > clientWidth) instead of relying on
// antd's built-in ellipsis-tooltip auto-detection, which doesn't reliably
// fire inside this flex layout — so the tooltip only wraps labels that are
// actually cut off.
const LabelCell = ({ label, height, width, align }) => {
  const textRef = React.useRef(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useLayoutEffect(() => {
    const el = textRef.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [label, width]);

  const content = (
    <RowLabelText ref={textRef} $height={height} $width={width} $align={align}>
      {label}
    </RowLabelText>
  );

  if (!isTruncated) return content;

  return <Tooltip title={label}>{content}</Tooltip>;
};

const ROW_HEIGHT = 26;
const ROW_GAP = 12;
const LABEL_WIDTH = 150;
const AXIS_AREA_HEIGHT = 32; // axis row's margin-top + padding-top + tick-label line + border

// Every row's height is an explicit inline style (not inferred from font
// metrics), so it can be shrunk deterministically to fit exactly N rows into
// a target `height` with no scrollbar.
const fitRowsToHeight = (rowCount, height) => {
  if (!height) return { rowHeight: ROW_HEIGHT, rowGap: ROW_GAP, barThickness: 10 };
  const available = height - AXIS_AREA_HEIGHT;
  const rowGap = Math.max(4, Math.min(ROW_GAP, Math.floor((available / rowCount) * 0.3)));
  const rowHeight = Math.max(14, Math.floor((available - (rowCount - 1) * rowGap) / rowCount));
  const barThickness = Math.max(4, Math.min(10, rowHeight - 8));
  return { rowHeight, rowGap, barThickness };
};

// "Nice round number" tick step — used so a value-scale chart (e.g. $ / hrs)
// gets tidy ticks (0, 200K, 400K, ...) instead of splitting the raw max into
// 5 odd numbers.
const niceTicks = (max, count = 5) => {
  if (!max || max <= 0) return [0, 1];
  const rawStep = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const niceNormalized = normalized < 1.5 ? 1 : normalized < 3 ? 2 : normalized < 7 ? 5 : 10;
  const step = niceNormalized * magnitude;
  const axisMax = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = 0; v <= axisMax + step / 2; v += step) ticks.push(Math.round(v * 100) / 100);
  return ticks;
};

// Horizontal bar list with an axis (gridlines + tick labels): label column on
// the left, bars + a shared 0%..100% (or nice-value) axis on the right.
//
// `height`, when given, shrinks row height/gap/bar thickness (via
// fitRowsToHeight) so however many rows are passed render inside that exact
// pixel height with no scrollbar. Pass the same `height` to two side-by-side
// charts with a different row count (6 Functions vs 9 Sub-Functions) to make
// them end up pixel-identical.
const RankedProgressBars = ({
  labels,
  values,
  valueFormatter = (v) => v,
  basis,
  accentColor = CHART_ACCENT_COLOR,
  greyStart = CHART_GREY_START,
  greyEnd = CHART_GREY_END,
  height,
  labelWidth = LABEL_WIDTH,
  labelAlign = "right",
}) => {
  const ranked = labels.map((label, i) => ({ label, value: values[i] })).sort(byValueDesc);

  const dataMax = Math.max(...ranked.map((r) => r.value), 1);
  const ticks = basis ? [0, 0.2, 0.4, 0.6, 0.8, 1].map((f) => basis * f) : niceTicks(dataMax);
  const axisMax = ticks[ticks.length - 1];
  const { rowHeight, rowGap, barThickness } = fitRowsToHeight(ranked.length, height);

  return (
    <RowsWrap>
      <LabelColumn $gap={rowGap}>
        {ranked.map((row) => (
          <LabelCell key={row.label} label={row.label} height={rowHeight} width={labelWidth} align={labelAlign} />
        ))}
      </LabelColumn>

      <ChartArea>
        <GridLinesRow>
          {ticks.map((t) => (
            <GridLine key={t} />
          ))}
        </GridLinesRow>

        <BarsColumn $gap={rowGap}>
          {ranked.map((row, i) => {
            // Only the #1 row gets the accent color; every other row fades
            // from a dark grey down to a light one, scaled to however many
            // rows there are — a continuous interpolation, so (unlike a
            // fixed-length color array indexed with `% length`) it can never
            // wrap back to the accent color once rows exceed some fixed
            // count (the bug that made a mid-ranked "Platform Engineering"
            // bar render blue in a 14-row chart).
            const fade = ranked.length > 2 ? (i - 1) / (ranked.length - 2) : 0;
            const barColor = i === 0 ? accentColor : interpolateColor(greyStart, greyEnd, Math.max(0, fade));
            // The value label sits on the card's white background, not on
            // the bar itself, so it needs its own readable color — a faded,
            // near-white bar tone would be illegible as text.
            const textColor = i === 0 ? accentColor : SB_COLORS.gray;
            const pct = (row.value / axisMax) * 100;
            return (
              <BarRow key={row.label} $height={rowHeight}>
                <BarTrack $pct={pct}>
                  <Progress percent={100} showInfo={false} strokeColor={barColor} strokeLinecap="butt" size={["100%", barThickness]} />
                </BarTrack>
                <BarValueText $color={textColor}>
                  {valueFormatter(row.value)}
                </BarValueText>
              </BarRow>
            );
          })}
        </BarsColumn>

        <AxisRow>
          {ticks.map((t) => (
            <AxisTickText key={t} type="secondary">
              {valueFormatter(t)}
            </AxisTickText>
          ))}
        </AxisRow>
      </ChartArea>
    </RowsWrap>
  );
};

export default RankedProgressBars;
